export type ThemeActivationState = 'active' | 'available' | 'unavailable' | 'unknown';
export type ThemeActivationStatus = ReadonlyMap<string, ThemeActivationState> | null;

// The CDN App Bridge supports this API; the installed package's types predate it.
type AppApi = { extensions: () => Promise<unknown> };
const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

// Shopify reports theme activations for the published theme only. Match block
// filenames, not display names or the enclosing extension's handle.
function parseActivations(value: unknown): ThemeActivationStatus {
    if (!Array.isArray(value)) return null;
    const states = new Map<string, ThemeActivationState>();
    for (const extension of value) {
        if (!isRecord(extension) || typeof extension.type !== 'string') return null;
        if (extension.type !== 'theme_app_extension') continue;
        if (!Array.isArray(extension.activations)) return null;
        for (const block of extension.activations) {
            if (!isRecord(block) || typeof block.handle !== 'string') return null;
            const state = block.status === 'active' || block.status === 'available' || block.status === 'unavailable'
                ? block.status : 'unknown';
            states.set(block.handle, state);
        }
    }
    return states;
}

export function getThemeActivationState(states: ThemeActivationStatus, handle: string): ThemeActivationState {
    // An empty successful response means this app has no activated blocks.
    // A failed/unavailable API must never be interpreted as an inactive block.
    return states === null ? 'unknown' : states.get(handle) ?? 'available';
}

export async function loadThemeActivationStatus(): Promise<ThemeActivationStatus> {
    const app = (window.shopify as unknown as { app?: AppApi } | undefined)?.app;
    if (typeof app?.extensions !== 'function') return null;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
        return await Promise.race([
            app.extensions().then(parseActivations),
            new Promise<null>(resolve => { timeout = setTimeout(() => resolve(null), 10000); }),
        ]);
    } catch {
        return null;
    } finally {
        clearTimeout(timeout);
    }
}
